import React, {useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {TableColumn, TableSettings} from "../../../../tableSettings";
import AbstractListView, {
  DataFetchResult, ExportRowRenderer, ListCustomFilterRenderer, ListOrderBy, ListRowRenderer
} from "../../../common/AbstractListView";

import lockRed from '../../../../img/lock_red.png';
import constants from '../../../../constants';
import {Search} from "../../../../filtering";

import {NavigationType, openNewPage} from '../../../../util/navigationHelpers';

import {
  DateRenderer, DateTimeRenderer, FloatRenderer,
  NullableCheckboxIconRenderer, TextAlignLeftRenderer, TextRenderer
} from '../../../../uiControls/SmartTableRenderers';
import UniqueLinkButton from '../../../../uiControls/UniqueLinkButton';
import PrimaryButton from '../../../../ui/PrimaryButton';
import useGlobalYesNoDialog from '../../../../hooks/useGlobalYesNoDialog';
import ApplicationContext from '../../../../ApplicationContext';
import JobListViewContext, { RightClickContext } from '../../../common/JobListViewContext';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../../../ui/ActionButton';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import {JobNames} from "../../../../framework.core/JobNames";
import {prepaidExpenseAdjustmentList} from "../../tableSettings";
import ListViewShortcutMenu from '../../../common/ListViewShortcutMenu';
import { AdjPrepaidExpenseListItem } from '../../../../fms.data/models/AdjPrepaidExpenseListItem';
import * as XCol from '../../../../framework.core/XExportColumn';
import ToolboxMenuItem from '../../../../uiControls/ToolboxMenuItem';
import Menu, { IfmsMenuItem } from '../../../../ui/menu/Menu';
import JobListSuntekCommand from '../../../common/JobListSuntekCommand';
import { FmsSecTask } from '../../../../fms.core/constants';
import { SuntekSettings, SystemSettings } from '../../../../ifmsTypes';
import { findSecurityTask } from '../../../../util';
import { useJournal } from '../journal/JournalListView';
import useSuntekSettings from '../../../../hooks/useSuntekSettings';
import { StatusNotUseFileLockedRenderer } from '../../../common/commonListRenderers';

function IndicatorRenderer({ data, row}:any) {
  const l = data[row.index];
  let dataTooltip = '';
  const lStatus = (l as any).status as string;
  if (lStatus === 'PC') dataTooltip = 'Period Close';

  let content: React.ReactNode = <></>;
  if (lStatus === 'PC')
    content = <img height="7px" src={lockRed} id={'redIcon'} style={{ position: 'relative', top: '0px' }} alt="" />;

  return (
    <div className="ifms-table-default" id={`ifms-table-default-${row.index}`} data-tooltip={dataTooltip} style={{ width: '100%', minHeight: '22px' }}>{content}</div>
  );
}

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const history = useHistory();
  const operation = 'accounting';

  const selectedElement = jobListContext.selectedElement as AdjPrepaidExpenseListItem;

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

  async function create() {
    if (jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acPrepaidExpenseAdjustment/add`)) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    openNewPage(history, `/${operation}/acPrepaidExpenseAdjustment/add`, NavigationType.newPage);
  }

  async function deleteEntity() {
    if (!selectedElement) {
      await showGlobalOkDialog('Cannot delete!',false,null,'warning');
      return;
    }

    if (
      jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acPrepaidExpenseAdjustment/edit/${selectedElement.id}`) || 
      jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acPrepaidExpenseAdjustment/view/${selectedElement.id}`)
    ) {
      await showGlobalOkDialog('Need to close Prepaid Expense Entry first', false, null, "warning");
      return;
    }
  
    const doDelete = await showGlobalYesNoDialog(
      <div>
        <h3>Confirm to Delete?</h3>
      </div>
    ,false, 0, true, 'warning');

    if (doDelete) {
      const apiResult = await appContext.api.deleteJobDetail(JobNames.PrepaidExpenseAdjustment, {
        id: selectedElement.id,
        pimsLinkId: 0
      });
      if (apiResult && apiResult.success) {
        jobListContext.reloadData();
      } else {
        const message = apiResult?.message ?? 'Cannot delete!';
        await showGlobalOkDialog(message, false,null,'warning');
      }
    }
  }

  async function edit() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/${operation}/acPrepaidExpenseAdjustment/edit/${id}`;
    const viewUrl = `/${operation}/acPrepaidExpenseAdjustment/view/${id}`;
    // if (jobListContext.hasOpenedScreenStartingWithUrlWithoutMe(`/${operation}/acPrepaidExpenseAdjustment/edit`, editUrl)) {
    //   await showGlobalOkDialog(<p>Edit operation is already in progress</p>);
    //   return;
    // }

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(editUrl, [editUrl, viewUrl]);
  }

  async function view() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;

    const editUrl = `/${operation}/acPrepaidExpenseAdjustment/edit/${id}`;
    const viewUrl = `/${operation}/acPrepaidExpenseAdjustment/view/${id}`;

    const listSec = findSecurityTask(appContext, FmsSecTask.AcctPrepaidAdjustment);
    const urls = [viewUrl];
    if (listSec?.grantEdit) urls.push(editUrl)

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(viewUrl, urls);
  }

  async function copy() {
    if (!selectedElement) {
      return;
    }

    if (jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acPrepaidExpenseAdjustment/add`)) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    const id = selectedElement.id;
    openNewPage(history, `/${operation}/acPrepaidExpenseAdjustment/add?copyId=${id}`, NavigationType.newPage);
  }

  return {create, deleteEntity, edit, view, copy, operation};
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { create, deleteEntity, edit, view } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.AcctPrepaidAdjustment);
  //const selectedElement = jobListContext.selectedElement as AdjPrepaidExpenseListItem;  

  const items = [
    { label: 'New', shortcut: 'F2', separator: false, hidden: !listSec?.grantAdd },
    { label: 'View', shortcut: 'F9', separator: !listSec?.grantEdit && !listSec?.grantDel },
    { label: 'Edit', shortcut: 'F3', separator: !listSec?.grantDel, hidden: !listSec?.grantEdit },
    { label: 'Delete', shortcut: '', separator: true, hidden: !listSec?.grantDel},
    { label: 'Search', shortcut: 'F5', separator: false },
    { label: constants.Doc_Server, shortcut: 'F8', separator: true, newCol: true },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem name="acPrepaidExpense-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
    hidden: i?.hidden ?? false,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        create();
        break;
      case 1:
        view();
        break;
      case 2:
        edit();
        break;
      case 3:
        deleteEntity();
        break;
      case 4:
        jobListContext.triggerTableSearch('F5');
        break;
      case 5:
        jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
        break;
      case 6:
        jobListContext.reloadData();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}


function ListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const selectedElement = jobListContext.selectedElement as AdjPrepaidExpenseListItem;
  const listSec = findSecurityTask(appContext, FmsSecTask.AcctPrepaidAdjustment);

  const {edit, view, deleteEntity, operation} = useSelectedOperations();

  function hasOpenedViewOrEditFor(id: number) {
    return jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acPrepaidExpenseAdjustment/edit/${id}`) || jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acPrepaidExpenseAdjustment/view/${id}`);
  }

  const hasSelectedRow = !!selectedElement;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel;
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;

  return (
    <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}>
      <div className="ifms-common-no-vertical-separator">
        <UniqueLinkButton name='acPrepaidExpense-list-add-button' display="primary" size="regular" link={`/${operation}/acPrepaidExpenseAdjustment/add`} disabled={!canBeAdd} label="New" />&nbsp;
        <PrimaryButton name='acPrepaidExpense-list-view-button' size="regular" disabled={!hasSelectedRow} onClick={view}>View</PrimaryButton>&nbsp;
        <PrimaryButton name='acPrepaidExpense-list-edit-button' size="regular" disabled={!canBeEdit} onClick={edit}>Edit</PrimaryButton>&nbsp;
        <PrimaryButton name='acPrepaidExpense-list-delete-button' size="regular" disabled={!canBeDeleted} onClick={deleteEntity}>Delete</PrimaryButton>
      </div>
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.AcctPrepaidAdjustment);

  const {create, edit, view} = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === 'F6') {

    } else if (unprocessedFkey === "F2") {
      if(listSec?.grantAdd)
        create();
    } else if (unprocessedFkey === "F3") {
      if(listSec?.grantEdit)
        edit();
    } else if (unprocessedFkey === "F9") {
      view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } else if (unprocessedFkey === "F8") {
      jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F6', 'F2', 'F3', 'F9', 'F11', 'F8'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <></>
  );
}

function formatData(data:Array<AdjPrepaidExpenseListItem>) {
  return data.map(d => ({
    ...d,
  }));
}

function TrancationImpactButtonCommands({ userId } : {userId: string}) {
  const jobListContext = useContext(JobListViewContext);
  const selectedElement = jobListContext.selectedElement as AdjPrepaidExpenseListItem;
  const {switchJournal} = useJournal();
  const suntekSettings = useSuntekSettings();
  
  if (!suntekSettings.general?.allowViewJournalBillings && !userId.startsWith('@')) {
    return null;
  }

  return (
    <PrimaryButton
      size="regular"
      onClick={()=> switchJournal(JobNames.PrepaidExpenseAdjustment, selectedElement.id)}>
      {constants.List_Journal}
    </PrimaryButton>
  );
}

/**
 * Concrete implementation for Prepaid Expense Adjustment List screen
 *
 * @class PrepaidExpenseAdjustmentListView
 * @extends {AbstractListView}
 */
class PrepaidExpenseAdjustmentListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.PrepaidExpenseAdjustment,
      'AdjPrepaidExpenseListItem',
      AdjPrepaidExpenseListItem.calculateSearchParameters(search,AdjPrepaidExpenseListItem.MapSearchFields),
      1,
      search.pageSize,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      {
        accessor: 'statusNotUseFileLocked',
        renderer: StatusNotUseFileLockedRenderer,
        searchValueExtractor: (v:any|null|undefined) => v,
      },
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(opType:string, systemSettings:SystemSettings, suntekSettings:SuntekSettings): ListRowRenderer[] {
    const useBranch = systemSettings?.enableBranch ?? false;
    
    return AdjPrepaidExpenseListItem.getExportCollection(useBranch).map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.type === XCol.XCellType.Double) colRenderer.Cell = (props:any) => <FloatRenderer {...props} decimals={2} />;
      else if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;

      if (x.dataIndex === "status") {
        colRenderer.Cell = IndicatorRenderer;
      }

      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'updated',
      orderByAscending: false,
    };
  }

  getTableSettings(opType:string, systemSettings:SystemSettings, suntekSettings:SuntekSettings): TableSettings {
    const tsettings =  prepaidExpenseAdjustmentList;
    const useBranch = systemSettings?.enableBranch ?? false;
    const cols = AdjPrepaidExpenseListItem.getExportCollection(useBranch);
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: AdjPrepaidExpenseListItem) {
    return row && row.id ? `/accounting/acPrepaidExpenseAdjustment/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Prepaid Exp Adjust List';
  };

  renderAdditionalCommands() {
    return <JobListSuntekCommand jobName={JobNames.PrepaidExpenseAdjustment} keyProp='id' showAdjustBalance={false} />;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }

  hasMemo(): boolean {
    return false;
  }
  
  transactionImpactBtn(userId: string): React.ReactNode {
    return <TrancationImpactButtonCommands userId={userId} />
  }
}

export default PrepaidExpenseAdjustmentListView;